import { action, computed, observable } from "mobx";
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import uuidv4 from "uuid/v4";
import FlashMessageStore from "~/stores/FlashMessageStore";
import AwsS3SignedUrlStore from "~/stores/AwsS3/AwsS3SignedUrlStore";
import AwsS3UploadSignedUrlStore from "~/stores/AwsS3/AwsS3UploadSignedUrlStore";
import Immersive3dTourProjectStore from "~/stores/Immersive3dTour/Immersive3dTourProjectStore";
import Immersive3dTourReplacePanoramaProcessStore from "~/stores/Immersive3dTour/Immersive3dTourReplacePanoramaProcessStore";
import Immersive3dTourUploadedPanoramaCheckStore from "~/stores/Immersive3dTour/Immersive3dTourUploadedPanoramaCheckStore";
import Immersive3dTourPanoramaZipDownloadStore from "~/stores/Immersive3dTour/Immersive3dTourPanoramaZipDownloadStore";
import ProcessingImageStore from "~/stores/ProcessingImageStore";

class Immersive3dTourTabReplacePanoramaStore {
  rootStore = null;

  /**
   * @property {Immersive3dTourStore|null}
   */
  immersive3dTourStore = null;

  /**
   * @property {AwsS3SignedUrlStore|null}
   */
  awsS3SignedUrlStore = null;

  /**
   * @property {AwsS3UploadSignedUrlStore|null}
   */
  awsS3UploadSignedUrlStore = null;

  /**
   * @property {Immersive3dTourProjectStore|null}
   */
  immersive3dTourProjectStore = null;

  /**
   * @property {Immersive3dTourReplacePanoramaProcessStore|null}
   */
  immersive3dTourReplacePanoramaProcessStore = null;

  /**
   * @property {Immersive3dTourUploadedPanoramaCheckStore|null}
   */
  immersive3dTourUploadedPanoramaCheckStore = null;

  /**
   * @property {Immersive3dTourPanoramaZipDownloadStore|null}
   */
  immersive3dTourPanoramaZipDownloadStore = null;

  /**
   * @property {FlashMessageStore|null}
   */
  flashMessageStore = null;

  /**
   * @property {ProcessingImageStore}
   */
  processingImageStore;

  /**
   * @property {boolean}
   */
  @observable isLoading = false;

  /**
   * @property {Array}
   */
  @observable cachedImmersive3dTourOriginalImages = [];

  /**
   * @property {number|null}
   */
  @observable activeSection = "immersive3dTourProject";

  /**
   * @property {number|null}
   */
  @observable replacePanoramaId = null;

  /**
   * @property {Array}
   */
  @observable replacePanoramaData = [];

  /**
   * @property {Array}
   */
  @observable immersive3dTourReplacePanoramaSignedUrls = [];

  /**
   * @property {Array} A list of files to upload
   */
  @observable uppyFiles = [];

  @observable uploader;

  /**
   * @property {boolean}
   */
  @observable isUploading = false;

  constructor(immersive3dTourStore) {
    this.immersive3dTourStore = immersive3dTourStore;
    this.awsS3SignedUrlStore = new AwsS3SignedUrlStore();
    this.awsS3UploadSignedUrlStore = new AwsS3UploadSignedUrlStore();
    this.immersive3dTourProjectStore = new Immersive3dTourProjectStore();
    this.immersive3dTourProjectStore.tourId = this.immersive3dTourStore.tourId;
    this.immersive3dTourReplacePanoramaProcessStore = new Immersive3dTourReplacePanoramaProcessStore();
    this.immersive3dTourReplacePanoramaProcessStore.tourId = this.immersive3dTourStore.tourId;
    this.immersive3dTourUploadedPanoramaCheckStore = new Immersive3dTourUploadedPanoramaCheckStore();
    this.immersive3dTourPanoramaZipDownloadStore = new Immersive3dTourPanoramaZipDownloadStore();
    this.immersive3dTourPanoramaZipDownloadStore.tourId = this.immersive3dTourStore.tourId;
    this.processingImageStore = new ProcessingImageStore(this);

    this.flashMessageStore = new FlashMessageStore(10000);
  }

  setRootStore(rootStore) {
    this.rootStore = rootStore;
  }

  @action
  load() {
    this.isLoading = true;

    this.reset();

    this.immersive3dTourProjectStore.load().finally(() => {
      this.isLoading = false;
    });
  }

  getCachedImmersive3dTourOriginalImage(panoramaId) {
    return this.cachedImmersive3dTourOriginalImages.find(
      immersive3dTourOriginalImage => immersive3dTourOriginalImage.id === panoramaId,
    );
  }

  getImmersive3dTourOriginalImageSignedUrl(panoramaId) {
    const panorama = this.immersive3dTourProjectStore.findPanoramaById(panoramaId);

    if (!panorama) {
      return Promise.resolve();
    }

    const { immersive3dTour } = this.immersive3dTourStore;
    const { tourId } = immersive3dTour;

    return this.awsS3SignedUrlStore
      .getOne({
        implementation: "immersive3dTours",
        key: `${tourId}/images/originals/${this.getPanoramaIdWithRevision(panorama)}.jpg`,
        responseCacheControl: "no-cache",
      })
      .then(data => {
        this.removeItemsFromPropertyArrayByPanoramaId("cachedImmersive3dTourOriginalImages", panorama.id);

        // Add to cache
        this.cachedImmersive3dTourOriginalImages.push({
          id: panorama.id,
          url: data.url,
        });

        return data;
      });
  }

  getImmersive3dTourReplacePanoramaSignedUrl(panoramaId) {
    return this.immersive3dTourReplacePanoramaSignedUrls.find(
      immersive3dTourReplacePanoramaSignedUrl => immersive3dTourReplacePanoramaSignedUrl.id === panoramaId,
    );
  }

  @action
  removeItemsFromPropertyArrayByPanoramaId(propertyName, panoramaId) {
    this[propertyName] = this[propertyName].filter(item => {
      return item.id !== panoramaId;
    });
  }

  @action
  goToUploadReplacePanoramaSection(panoramaId) {
    this.createUploader();

    this.setReplacePanoramaId(panoramaId);
    this.setActiveSection("uploadReplacePanorama");
  }

  @action
  goToImmersive3dTourProjectSection(resetReplacePanoramaData = false) {
    if (resetReplacePanoramaData) {
      this.replacePanoramaData = [];
    }
    this.closeUploader();
    this.setReplacePanoramaId(null);
    this.setActiveSection("immersive3dTourProject");
  }

  @action
  goToUploadMultipleReplacePanoramaSection() {
    this.createUploader(true);

    this.setActiveSection("uploadMultipleReplacePanorama");
  }

  @action
  setActiveSection(section) {
    this.activeSection = section;
  }

  @action
  setReplacePanoramaId(replacePanoramaId) {
    this.replacePanoramaId = replacePanoramaId;
  }

  @action
  createUploader(multipleUpload = false) {
    this.uppyFiles = [];

    const implementation = "immersive3dTourReplacePanoramas";

    const uppySettings = {
      allowMultipleUploads: false,
      restrictions: {
        allowedFileTypes: [".jpg", ".jpeg"],
      },
    };

    if (multipleUpload) {
      uppySettings.allowMultipleUploads = true;

      // eslint-disable-next-line no-unused-vars
      uppySettings.onBeforeFileAdded = (currentFile, files) => {
        if (this.immersive3dTourProjectPanoramaFilenames.indexOf(currentFile.name.toLowerCase()) === -1) {
          this.uploader.info(
            `The image file name needs to be one of the panorama ids of the Immersive 3D Tour instead of : '${currentFile.name}'`,
            "error",
            5000,
          );

          return false;
        }

        this.addFile(currentFile.name);

        return true;
      };
    } else {
      uppySettings.restrictions.maxNumberOfFiles = 1;

      // eslint-disable-next-line no-unused-vars
      uppySettings.onBeforeFileAdded = (currentFile, files) => {
        this.addFile(currentFile.name);

        return true;
      };
    }

    this.uploader = Uppy(uppySettings);

    const { immersive3dTour } = this.immersive3dTourStore;
    const { tourId } = immersive3dTour;

    const getAwsS3UploadSignedUrl = id => {
      return this.awsS3UploadSignedUrlStore.getOne({
        implementation,
        key: `immersive3dTourReplacePanoramas/${id}/${uuidv4()}.jpg`,
        contentType: "image/jpeg",
      });
    };

    this.uploader.use(AwsS3, {
      // eslint-disable-next-line no-unused-vars
      getUploadParameters(file) {
        // Send a request to our PHP signing endpoint.
        return getAwsS3UploadSignedUrl(tourId).then(data => {
          // Return an object in the correct shape.
          return {
            method: "PUT",
            url: data.url,
            fields: null,
            // Provide content type header required by S3
            headers: {
              "Content-Type": "image/jpeg",
            },
          };
        });
      },
    });

    if (multipleUpload) {
      this.uploader.on("upload-success", (file, response) => {
        this.onFileUploaded(file, response.body, true);
      });
    } else {
      this.uploader.on("upload-success", (file, response) => {
        this.onFileUploaded(file, response.body);
      });
    }

    this.uploader.on("upload", () => {
      this.isUploading = true;
    });

    // eslint-disable-next-line no-unused-vars
    this.uploader.on("complete", result => {
      this.isUploading = false;
      // Uploading is complete, the processing of the images is still on
      // We do not want to show the uppy dashboard anymore
      this.isLoading = true;
    });
  }

  @action
  closeUploader() {
    this.uploader.close();
  }

  @action
  async onFileUploaded(file, response, multipleUpload) {
    if (!file.id || !response.location) {
      return false;
    }

    const panoramaId = !multipleUpload ? this.replacePanoramaId : parseInt(file.name.split(".")[0], 10);

    // Get the key of the uploaded file (the part after the bucket)
    const url = new URL(response.location);
    const urlPathnameParts = url.pathname.split("/");
    const source = urlPathnameParts.splice(2).join("/");

    const result = await this.checkImmersive3dTourUploadedPanorama(source);

    if (typeof result.metadata.equirectangular === "undefined" || !result.metadata.equirectangular) {
      this.completeAfterLoadingProcedure(file.name, "equirectangular");

      return false;
    }

    if (typeof result.metadata.width === "undefined" || result.metadata.width < 3000) {
      this.completeAfterLoadingProcedure(file.name, "width");

      return false;
    }

    // Remove existing / overwrite
    this.removeItemsFromPropertyArrayByPanoramaId("replacePanoramaData", panoramaId);

    this.replacePanoramaData.push({
      id: panoramaId,
      source,
    });

    // Remove existing / overwrite
    this.removeItemsFromPropertyArrayByPanoramaId("immersive3dTourReplacePanoramaSignedUrls", panoramaId);

    this.immersive3dTourReplacePanoramaSignedUrls.push({
      id: panoramaId,
      url: result.url,
    });

    this.getImmersive3dTourOriginalImageSignedUrl(panoramaId).then(() => {
      this.completeAfterLoadingProcedure(file.name);
    });

    return true;
  }

  completeAfterLoadingProcedure(fileName, error = null) {
    const foundIndex = this.uppyFiles.findIndex(item => item.filename === fileName);

    // If there was no error, we want to remove the file from our list to upload/process
    if (!error) {
      this.uppyFiles.splice(foundIndex, 1);
    } else {
      this.uppyFiles[foundIndex].errors.push(error);
    }

    // Check if there are still files without errors (not processed yet)
    if (
      this.uppyFiles.filter(uppyFile => uppyFile.filename !== fileName && uppyFile.errors.length === 0).length === 0
    ) {
      this.uploader.close();
      this.goToImmersive3dTourProjectSection();
      this.isLoading = false;
    }
  }

  checkImmersive3dTourUploadedPanorama(source) {
    return this.immersive3dTourUploadedPanoramaCheckStore
      .getOne({
        implementation: "immersive3dTourReplacePanoramas",
        key: `${source}`,
      })
      .then(response => {
        return response.data;
      });
  }

  getImmersive3dTourCubeUrl(panorama) {
    const { immersive3dTour } = this.immersive3dTourStore;
    const { tourId } = immersive3dTour;

    // New projects do not have necessarily the 256 resolution
    // Let's find the minimum resolution from the available resolutions
    const minimumResolution = Math.min(...panorama.resolutions);
    // Add x-y at the file name if the panorama has tiles
    const tiles = typeof panorama.tiles === "boolean" && panorama.tiles === true;
    const fileName = tiles ? "nz_0_0" : "nz";

    return `${window.immersive3dTourProjectRoot}/${tourId}/images/cubes/${this.getPanoramaIdWithRevision(
      panorama,
      false,
    )}/${minimumResolution}/${fileName}.jpg`;
  }

  @action
  process() {
    if (this.orderedReplacePanoramaData.length === 0) {
      return false;
    }

    this.isLoading = true;

    return this.immersive3dTourReplacePanoramaProcessStore
      .update({
        replacePanoramaData: this.orderedReplacePanoramaData,
      })
      .then(response => {
        this.immersive3dTourStore.processEntity(response.data.immersive3dTour);

        // Go back to other workflow
        this.rootStore.setIsReplacePanoramaPage(false);
        this.rootStore.processingAutoRefresh();

        this.reset();
      })
      .catch(response => {
        if (response.status === "fail") {
          const [error] = response.errors;
          throw Error(error);
        } else {
          this.flashMessageStore.setMessage(response.message, "negative");
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  reset() {
    this.cachedImmersive3dTourOriginalImages = [];
    this.replacePanoramaData = [];
    this.activeSection = "immersive3dTourProject";
    this.replacePanoramaId = null;
    this.uploadedReplacePanoramaSignedUrls = {};
    this.uppyFiles = [];
  }

  @computed
  get immersive3dTourProjectPanoramaFilenames() {
    const { immersive3dTourProject } = this.immersive3dTourProjectStore;
    const { panos } = immersive3dTourProject;

    const immersive3dTourProjectPanoramaFilenames = [];

    panos.forEach(panorama => {
      immersive3dTourProjectPanoramaFilenames.push(`${panorama.id}.jpg`);
    });

    return immersive3dTourProjectPanoramaFilenames;
  }

  @action
  addFile(filename) {
    this.uppyFiles.push({
      filename,
      errors: [],
    });
  }

  @computed
  get uppyFileErrors() {
    const uppyFileErrors = [];
    this.uppyFiles
      .filter(item => item.errors.length > 0)
      .forEach(uppyFile => {
        uppyFile.errors.forEach(error => {
          if (error === "equirectangular") {
            uppyFileErrors.push(
              `The image with file name '${uppyFile.filename}' does not seem to be equirectangular. Please check to make sure the image width is exactly 2 times the height.`,
            );
          }

          if (error === "width") {
            uppyFileErrors.push(
              `The image with file name '${uppyFile.filename}' needs to have width of at least 3000 pixels.`,
            );
          }
        });
      });

    return uppyFileErrors;
  }

  @computed
  get orderedReplacePanoramaData() {
    return this.replacePanoramaData.slice().sort((a, b) => a.id - b.id);
  }

  @computed
  get imageUrls() {
    const imageUrls = [];

    this.cachedImmersive3dTourOriginalImages.forEach(cachedImmersive3dTourOriginalImage => {
      imageUrls.push(cachedImmersive3dTourOriginalImage.url);
    });

    return imageUrls;
  }

  getPanoramaIdWithRevision(panorama, withEnhanced = true) {
    let revision = "";

    if (typeof panorama.image.revision !== "undefined" && panorama.image.revision !== null) {
      revision = `_${withEnhanced && panorama.image.isEnhanced ? "e" : ""}${panorama.image.revision}`;
    }

    return `${panorama.image.id}${revision}`;
  }

  openProcessingImageModal(panoramaId) {
    this.isLoading = true;

    // Fill the cache with the Immersive 3d Tour original images (signed urls)
    this.fillCacheWithImmersive3dTourOriginalImageSignedUrls().then(() => {
      this.isLoading = false;

      const immersive3dTourOriginalImage = this.getCachedImmersive3dTourOriginalImage(panoramaId);

      if (typeof immersive3dTourOriginalImage === "undefined") {
        throw new Error("Cannot retrieve the signed url for this panorama.");
      }

      this.processingImageStore.openModal(immersive3dTourOriginalImage.url);
    });
  }

  fillCacheWithImmersive3dTourOriginalImageSignedUrls() {
    const { immersive3dTour } = this.immersive3dTourStore;
    const { tourId } = immersive3dTour;

    const { immersive3dTourProject } = this.immersive3dTourProjectStore;
    const { panos } = immersive3dTourProject;

    if (panos.length === 0) {
      return Promise.resolve();
    }

    const promises = [];

    // Gather the panorama ids
    panos.forEach(panorama => {
      // If the signed url exists, continue
      if (typeof this.cachedImmersive3dTourOriginalImages.find(item => item.id === panorama.id) !== "undefined") {
        return;
      }

      const promise = this.awsS3SignedUrlStore
        .getOne({
          implementation: "immersive3dTours",
          key: `${tourId}/images/originals/${this.getPanoramaIdWithRevision(panorama)}.jpg`,
          responseCacheControl: "no-cache",
        })
        .then(data => {
          this.removeItemsFromPropertyArrayByPanoramaId("cachedImmersive3dTourOriginalImages", panorama.id);

          // Add to cache
          this.cachedImmersive3dTourOriginalImages.push({
            id: panorama.id,
            url: data.url,
          });
        });

      promises.push(promise);
    });

    return Promise.all(promises);
  }

  @action
  updateImageData(imageUrl, imageData) {
    const panorama = this.cachedImmersive3dTourOriginalImages.find(item => item.url === imageUrl);

    if (typeof panorama === "undefined") {
      throw new Error("Cannot find this signed url in cached Immersive 3D Tour original images.");
    }

    const implementation = "immersive3dTourReplacePanoramas";

    const { immersive3dTour } = this.immersive3dTourStore;
    const { tourId } = immersive3dTour;
    const key = `immersive3dTourReplacePanoramas/${tourId}/${uuidv4()}.jpg`;

    return this.awsS3UploadSignedUrlStore
      .getOne({
        implementation,
        key,
        contentType: "image/jpeg",
      })
      .then(awsS3UploadSignedUrlData => {
        const buffer = Buffer.from(imageData.replace(/^data:image\/\w+;base64,/, ""), "base64");

        const parameters = {
          method: "PUT",
          body: buffer,
          credentials: "omit",
          headers: {
            "Content-Type": "image/jpeg",
            "Content-Encoding": "base64",
          },
        };

        return fetch(awsS3UploadSignedUrlData.url, parameters).then(() => {
          return this.awsS3SignedUrlStore
            .getOne({
              implementation,
              key,
              responseCacheControl: "no-cache",
            })
            .then(awsS3SignedUrlData => {
              // Remove existing / overwrite
              this.removeItemsFromPropertyArrayByPanoramaId("replacePanoramaData", panorama.id);

              this.replacePanoramaData.push({
                id: panorama.id,
                source: key,
              });

              // Remove existing / overwrite
              this.removeItemsFromPropertyArrayByPanoramaId("immersive3dTourReplacePanoramaSignedUrls", panorama.id);

              this.immersive3dTourReplacePanoramaSignedUrls.push({
                id: panorama.id,
                url: awsS3SignedUrlData.url,
              });

              this.processingImageStore.showModal = false;
            });
        });
      });
  }
}

export default Immersive3dTourTabReplacePanoramaStore;
